// Mortgage calculations for Canada (semi-annual compounding).
import { effectivePeriodicRate, amortizedPayment, round2 } from "./finance-math";
import { MORTGAGE_RULES } from "../financial-data/canada";

export const PAYMENT_FREQUENCIES = {
  monthly: { label: "Monthly", perYear: 12 },
  semimonthly: { label: "Semi-monthly", perYear: 24 },
  biweekly: { label: "Bi-weekly", perYear: 26 },
  accelerated_biweekly: { label: "Accelerated bi-weekly", perYear: 26, accelerated: "monthly", divisor: 2 },
  weekly: { label: "Weekly", perYear: 52 },
  accelerated_weekly: { label: "Accelerated weekly", perYear: 52, accelerated: "monthly", divisor: 4 },
};

// principal, annualRate (%), amortizationYears, frequency key
export function mortgagePayment({ principal, annualRate, amortizationYears, frequency = "monthly" }) {
  const p = Math.max(0, Number(principal) || 0);
  const rate = Math.max(0, Number(annualRate) || 0) / 100;
  const years = Math.max(0, Number(amortizationYears) || 0);
  const freq = PAYMENT_FREQUENCIES[frequency] || PAYMENT_FREQUENCIES.monthly;

  const compounding = 2; // Canadian fixed mortgages compound semi-annually
  let payment;
  let periods = Math.round(years * freq.perYear);

  if (freq.accelerated) {
    // Accelerated = the equivalent monthly payment divided by 2 (biweekly) or 4 (weekly)
    const monthlyRate = effectivePeriodicRate(rate, compounding, 12);
    const monthlyPayment = amortizedPayment({ principal: p, periodicRate: monthlyRate, periods: years * 12 });
    payment = monthlyPayment / freq.divisor;
  } else {
    const periodicRate = effectivePeriodicRate(rate, compounding, freq.perYear);
    payment = amortizedPayment({ principal: p, periodicRate, periods });
  }

  // Build schedule by simulating with the true periodic rate of the chosen frequency.
  const periodicRate = effectivePeriodicRate(rate, compounding, freq.perYear);
  const schedule = [];
  let balance = p;
  let totalInterest = 0;
  let periodCount = 0;
  const maxPeriods = Math.round(years * freq.perYear) + (freq.accelerated ? freq.perYear * years : 0) + 10000;

  while (balance > 0.01 && periodCount < maxPeriods) {
    const interest = balance * periodicRate;
    let principalPaid = payment - interest;
    if (principalPaid > balance) principalPaid = balance;
    balance = balance - principalPaid;
    totalInterest += interest;
    periodCount += 1;
    schedule.push({
      period: periodCount,
      payment: round2(principalPaid + interest),
      principal: round2(principalPaid),
      interest: round2(interest),
      balance: round2(Math.max(0, balance)),
    });
    if (payment <= interest && rate > 0) break; // payment can't cover interest — avoid infinite loop
  }

  const payoffYears = periodCount / freq.perYear;
  const totalPaid = p + totalInterest;

  // Yearly aggregation for chart readability
  const yearly = [];
  const perYearCount = freq.perYear;
  for (let y = 0; y < Math.ceil(schedule.length / perYearCount); y++) {
    const slice = schedule.slice(y * perYearCount, (y + 1) * perYearCount);
    if (!slice.length) break;
    yearly.push({
      year: y + 1,
      principal: round2(slice.reduce((a, s) => a + s.principal, 0)),
      interest: round2(slice.reduce((a, s) => a + s.interest, 0)),
      balance: slice[slice.length - 1].balance,
    });
  }

  return {
    payment: round2(payment),
    frequencyLabel: freq.label,
    numberOfPayments: periodCount,
    totalInterest: round2(totalInterest),
    totalPaid: round2(totalPaid),
    payoffYears: round2(payoffYears),
    interestSaved: 0,
    schedule,
    yearly,
  };
}

// Down payment minimum by Canadian rules.
export function minimumDownPayment(price) {
  const p = Math.max(0, Number(price) || 0);
  if (p <= 500000) return p * 0.05;
  if (p < 1000000) return 500000 * 0.05 + (p - 500000) * 0.10;
  return p * 0.20;
}

// Affordability using GDS/TDS ratios and the stress test.
export function mortgageAffordability({
  annualIncome,
  monthlyDebts = 0,
  downPayment = 0,
  annualRate,
  amortizationYears = 25,
  propertyTaxAnnual = 4000,
  heatingMonthly = 150,
  gdsMax = MORTGAGE_RULES.gdsMax,
  tdsMax = MORTGAGE_RULES.tdsMax,
  useStressTest = true,
}) {
  const income = Math.max(0, Number(annualIncome) || 0);
  const monthlyIncome = income / 12;
  const contractRate = Math.max(0, Number(annualRate) || 0) / 100;
  const qualifyRate = useStressTest
    ? Math.max(contractRate + MORTGAGE_RULES.stressTestBuffer, MORTGAGE_RULES.stressTestFloor)
    : contractRate;

  const monthlyRate = effectivePeriodicRate(qualifyRate, 2, 12);
  const periods = amortizationYears * 12;
  const taxMonthly = (Number(propertyTaxAnnual) || 0) / 12;
  const heat = Number(heatingMonthly) || 0;

  // Max housing cost allowed by each ratio
  const gdsRoom = monthlyIncome * gdsMax; // covers P&I + tax + heat
  const tdsRoom = monthlyIncome * tdsMax - (Number(monthlyDebts) || 0); // covers housing + other debts
  const maxHousing = Math.max(0, Math.min(gdsRoom, tdsRoom));
  const maxPI = Math.max(0, maxHousing - taxMonthly - heat);

  // Back out max mortgage principal from max P&I payment
  let maxMortgage;
  if (monthlyRate === 0) {
    maxMortgage = maxPI * periods;
  } else {
    const g = Math.pow(1 + monthlyRate, periods);
    maxMortgage = (maxPI * (g - 1)) / (monthlyRate * g);
  }
  const dp = Math.max(0, Number(downPayment) || 0);
  const maxHomePrice = maxMortgage + dp;

  return {
    maxHomePrice: Math.max(0, round2(maxHomePrice)),
    maxMortgage: Math.max(0, round2(maxMortgage)),
    maxMonthlyPayment: round2(maxPI),
    qualifyingRate: round2(qualifyRate * 100),
    gdsRoom: round2(gdsRoom),
    tdsRoom: round2(tdsRoom),
    minDownForPrice: round2(minimumDownPayment(maxHomePrice)),
    downPayment: dp,
  };
}
